<script setup lang="ts">
/** Édition d'une demande d'accompagnement — réutilise les sections du brief. */
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import { ArrowLeftIcon, PencilSquareIcon } from '@heroicons/vue/24/outline'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { canEdit } from '@/v2/accompagnement/helpers/accompagnementPermissions'
import type {
  AccompClientRef,
  AccompContext,
  AccompDocument,
  AccompProjectConstraints,
  AccompRequest,
  AccompRequestType,
  AccompScope,
  AccompUrgency,
} from '@/v2/accompagnement/api/accompagnementTypes'
import AccompCreateFormAdmin from '@/v2/accompagnement/components/AccompCreateFormAdmin.vue'
import AccompCreateFormDemande from '@/v2/accompagnement/components/AccompCreateFormDemande.vue'
import AccompCreateFormObjective from '@/v2/accompagnement/components/AccompCreateFormObjective.vue'
import AccompCreateFormContext from '@/v2/accompagnement/components/AccompCreateFormContext.vue'
import AccompCreateFormScope from '@/v2/accompagnement/components/AccompCreateFormScope.vue'
import AccompCreateFormProjectConstraints from '@/v2/accompagnement/components/AccompCreateFormProjectConstraints.vue'
import AccompCreateFormDocuments from '@/v2/accompagnement/components/AccompCreateFormDocuments.vue'

const route = useRoute()
const router = useRouter()
const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const { current, currentLoading, currentError, currentUserUuid } = storeToRefs(store)

const id = computed(() => route.params.id as string)
const ready = ref(false)
const allowed = ref(false)
const submitted = ref(false)

const form = reactive({
  client: null as AccompClientRef | null,
  requestType: '' as AccompRequestType | '',
  urgency: 'NORMAL' as AccompUrgency,
  estimatedHours: null as number | null,
  objective: '',
  context: {
    activity: '',
    sites: null,
    collaborators: null,
    remoteWork: '',
    growth: '',
    constraints: '',
  } as AccompContext,
  scope: [] as AccompScope[],
  projectConstraints: {
    desiredDate: null,
    mandatoryDate: null,
    budgetKnown: false,
    budgetAmount: null,
    decisionMaker: '',
    competitorCount: null,
    tender: false,
  } as AccompProjectConstraints,
  availableDocuments: [] as AccompDocument[],
})

function fillForm(req: AccompRequest) {
  form.client = { ...req.client }
  form.requestType = req.requestType
  form.urgency = req.urgency
  form.estimatedHours = req.estimatedHours
  form.objective = req.objective
  form.context = { ...req.context }
  form.scope = [...req.scope]
  form.projectConstraints = { ...req.projectConstraints }
  form.availableDocuments = [...req.availableDocuments]
}

async function load() {
  ready.value = false
  await store.fetchOne(id.value)
  if (current.value) {
    allowed.value = canEdit(current.value, perms, currentUserUuid.value)
    if (allowed.value) fillForm(current.value)
  }
  ready.value = true
}

const errors = computed(() => ({
  client: submitted.value && !form.client ? 'Sélectionnez un client.' : undefined,
  requestType: submitted.value && !form.requestType ? 'Choisissez un type de demande.' : undefined,
  objective: submitted.value && !form.objective.trim() ? "L'objectif du client est requis." : undefined,
}))

const valid = computed(() => !!form.client && !!form.requestType && !!form.objective.trim())

async function submit() {
  submitted.value = true
  if (!valid.value || !form.client) return
  const res = await store.update(id.value, {
    client: form.client,
    requestType: form.requestType as AccompRequestType,
    urgency: form.urgency,
    estimatedHours: form.estimatedHours,
    objective: form.objective.trim(),
    context: form.context,
    scope: form.scope,
    projectConstraints: form.projectConstraints,
    availableDocuments: form.availableDocuments,
  })
  if (res) {
    toast.success('Demande mise à jour.')
    router.push(`/accompagnement/${id.value}`)
  } else {
    toast.error(store.actionError ?? 'Échec de la mise à jour.')
  }
}

onMounted(load)
watch(id, load)

// Le secteur d'activité (libellé NAF récupéré via le picker) alimente « Activité du
// client » s'il est encore vide — sans écraser une saisie manuelle.
watch(
  () => form.client?.nafLabel,
  (label) => {
    if (label && !form.context.activity.trim()) form.context.activity = label
  },
)
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-6 lg:p-8">
    <RouterLink :to="`/accompagnement/${id}`" class="mb-3 inline-flex items-center gap-1 text-sm text-base-content/70 hover:text-primary">
      <ArrowLeftIcon class="h-4 w-4" aria-hidden="true" />
      Retour à la demande
    </RouterLink>

    <header class="mb-6">
      <h1 class="flex items-center gap-2 text-2xl font-bold">
        <PencilSquareIcon class="h-6 w-6 text-primary" aria-hidden="true" />
        Éditer la demande
        <span v-if="current" class="tabular-nums text-base-content/60">{{ current.reference }}</span>
      </h1>
    </header>

    <div v-if="currentLoading || !ready" class="py-16 text-center">
      <span class="loading loading-spinner" aria-hidden="true"></span>
      <p class="mt-2 text-base-content/70">Chargement…</p>
    </div>
    <div v-else-if="currentError || !current" role="alert" class="alert alert-error">
      <span>Demande introuvable.</span>
      <RouterLink to="/accompagnement" class="btn btn-sm">Retour</RouterLink>
    </div>
    <div v-else-if="!allowed" role="alert" class="alert alert-warning">
      <span>Vous n'avez pas les droits pour éditer cette demande.</span>
      <RouterLink :to="`/accompagnement/${id}`" class="btn btn-sm">Retour</RouterLink>
    </div>

    <form
      v-else
      class="grid grid-cols-1 gap-5 md:grid-cols-2 md:items-stretch"
      novalidate
      @submit.prevent="submit"
    >
      <AccompCreateFormAdmin v-model:client="form.client" :client-error="errors.client" />
      <AccompCreateFormContext v-model:context="form.context" />
      <AccompCreateFormDemande
        v-model:request-type="form.requestType"
        v-model:urgency="form.urgency"
        v-model:estimated-hours="form.estimatedHours"
        :type-error="errors.requestType"
      />
      <AccompCreateFormObjective v-model:objective="form.objective" :objective-error="errors.objective" />
      <AccompCreateFormScope v-model:scope="form.scope" class="md:col-span-2" />
      <AccompCreateFormProjectConstraints v-model:constraints="form.projectConstraints" />
      <AccompCreateFormDocuments v-model:documents="form.availableDocuments" />

      <div class="flex items-center justify-end gap-2 md:col-span-2">
        <RouterLink :to="`/accompagnement/${id}`" class="btn btn-ghost">Annuler</RouterLink>
        <button type="submit" class="btn btn-primary" :disabled="store.actionLoading" :aria-busy="store.actionLoading">
          <span v-if="store.actionLoading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          Enregistrer
        </button>
      </div>
    </form>
  </div>
</template>
